import React, { useState, useMemo } from 'react';
import {
  BookOpen, ChevronRight, ChevronLeft, Check, Circle,
  Zap, Music, Sliders, ArrowRight, ArrowLeft,
  Lightbulb, Volume2, Layers, Wrench
} from 'lucide-react';
import { EARTH, TYPOGRAPHY, SECTION_COLORS, getSectionType, calculateDuration } from '../theme/constants.js';

function CoachingSidebar({
  isOpen,
  onToggle,
  section,
  allSections = [],
  projectTempo,
  projectKey,
  coachingState = {},
  onCoachingStateChange,
  onUpdateSectionStatus,
  onNavigateToSection
}) {
  const [activeTab, setActiveTab] = useState('workflow');

  const guidance = section?.productionGuidance || section?.sectionGuidance || null;
  const sectionType = section ? getSectionType(section.name || '') : 'default';
  const colors = SECTION_COLORS[sectionType] || SECTION_COLORS.default;

  // Completed steps keyed by section id
  const completedSteps = coachingState.completedSteps || {};
  const sectionCompleted = completedSteps[section?.id] || [];

  const toggleStep = (stepIndex) => {
    if (!section) return;
    const current = [...sectionCompleted];
    if (current.includes(stepIndex)) {
      current.splice(current.indexOf(stepIndex), 1);
    } else {
      current.push(stepIndex);
    }
    onCoachingStateChange?.({
      ...coachingState,
      completedSteps: { ...completedSteps, [section.id]: current }
    });
  };

  // Find current section index and neighbors
  const sectionIndex = allSections.findIndex(s => s.id === section?.id);
  const prevSection = sectionIndex > 0 ? allSections[sectionIndex - 1] : null;
  const nextSection = sectionIndex < allSections.length - 1 ? allSections[sectionIndex + 1] : null;

  // Energy arc data — prev, current, next
  const energyArc = useMemo(() => {
    if (!section) return [];
    const arc = [];
    if (prevSection) arc.push({ name: prevSection.name, energy: prevSection.energy || 0 });
    arc.push({ name: section.name, energy: section.energy || 0, current: true });
    if (nextSection) arc.push({ name: nextSection.name, energy: nextSection.energy || 0 });
    return arc;
  }, [section, prevSection, nextSection]);

  // Closed state — handled by unified RightPanelTabs in Studio.jsx
  if (!isOpen) return null;

  // No section selected
  if (!section) {
    return (
      <div className="fixed right-0 top-0 h-full w-80 shadow-xl z-20 flex flex-col" style={{ backgroundColor: '#fff', borderLeft: '1px solid ' + EARTH.stone[200] }}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.forest[50] }}>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: EARTH.forest[600] }} />
            <h3 className="font-semibold" style={{ color: EARTH.forest[800] }}>Coach</h3>
          </div>
          <button onClick={onToggle} className="p-1 rounded" style={{ color: EARTH.stone[500] }}>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: EARTH.stone[300] }} />
            <p className="text-sm" style={{ color: EARTH.stone[500] }}>Select a section to get production coaching</p>
          </div>
        </div>
      </div>
    );
  }

  const dawActions = guidance?.dawActions || [];
  const mixTips = guidance?.mixTips || [];
  const energyTip = guidance?.energyTip || '';
  const transitionTip = guidance?.transitionTip || '';
  const referenceTracks = guidance?.referenceTracks || [];
  const allStepsCompleted = dawActions.length > 0 && sectionCompleted.length >= dawActions.length;

  const tabs = [
    { id: 'workflow', label: 'DAW', icon: Wrench },
    { id: 'mix', label: 'Mix', icon: Sliders },
    { id: 'sound', label: 'Sound', icon: Volume2 },
    { id: 'flow', label: 'Flow', icon: ArrowRight },
  ];

  return (
    <div className="fixed right-0 top-0 h-full w-80 shadow-xl z-20 flex flex-col" style={{ backgroundColor: '#fff', borderLeft: '1px solid ' + EARTH.stone[200] }}>

      {/* Header — section info */}
      <div className="p-4 border-b" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.forest[50] }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: EARTH.forest[600] }} />
            <h3 className="font-semibold" style={{ color: EARTH.forest[800] }}>Coach</h3>
          </div>
          <button onClick={onToggle} className="p-1 rounded" style={{ color: EARTH.stone[500] }}>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Section name + meta */}
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide"
            style={{
              backgroundColor: colors.bg,
              color: colors.accent,
              border: '1px solid ' + colors.border
            }}
          >
            {sectionType}
          </span>
          <span className="font-medium text-sm" style={{ color: EARTH.stone[800] }}>
            {section.name}
          </span>
        </div>

        <div className="flex items-center gap-3 text-xs" style={{ color: EARTH.stone[500] }}>
          {section.bars && <span>{section.bars} bars</span>}
          {projectTempo && section.bars && (
            <span>{calculateDuration(section.bars, projectTempo)}</span>
          )}
          {section.energy != null && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {Math.round(section.energy * 100)}%
            </span>
          )}
          {projectKey && <span>{projectKey}</span>}
        </div>
      </div>

      {/* Energy Arc Mini */}
      {energyArc.length > 1 && (
        <div className="px-4 py-3" style={{ borderBottom: '1px solid ' + EARTH.stone[200], backgroundColor: EARTH.sand[50] }}>
          <div className="flex items-center gap-1 mb-2">
            <Zap className="w-3 h-3" style={{ color: EARTH.sand[600] }} />
            <span className="text-xs font-medium" style={{ color: EARTH.sand[700] }}>Energy Arc</span>
          </div>
          <div className="flex items-end gap-1 h-10">
            {energyArc.map((item, i) => {
              const height = Math.max(10, item.energy * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className="w-full rounded-t transition-all duration-300"
                    style={{
                      height: `${height}%`,
                      backgroundColor: item.current ? EARTH.terracotta[500] : EARTH.sand[300],
                      minHeight: '4px'
                    }}
                  />
                  <span className="text-[9px] truncate w-full text-center" style={{ color: EARTH.stone[500] }}>
                    {item.name.length > 8 ? item.name.slice(0, 7) + '…' : item.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: EARTH.stone[200] }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors"
              style={{
                color: isActive ? EARTH.forest[700] : EARTH.stone[500],
                borderBottom: isActive ? '2px solid ' + EARTH.forest[600] : '2px solid transparent',
                backgroundColor: isActive ? EARTH.forest[50] : 'transparent'
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content — scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── WORKFLOW TAB ── */}
        {activeTab === 'workflow' && (
          <>
            {/* DAW Actions checklist */}
            {dawActions.length > 0 ? (
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <Wrench className="w-3.5 h-3.5" style={{ color: EARTH.forest[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.forest[700] }}>
                    DAW Actions
                  </span>
                  <span className="text-xs ml-auto" style={{ color: EARTH.stone[400] }}>
                    {sectionCompleted.length}/{dawActions.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {dawActions.map((action, i) => {
                    const done = sectionCompleted.includes(i);
                    return (
                      <button
                        key={i}
                        onClick={() => toggleStep(i)}
                        className="w-full flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-all"
                        style={{
                          backgroundColor: done ? EARTH.sage[50] : EARTH.stone[50],
                          border: '1px solid ' + (done ? EARTH.sage[300] : EARTH.stone[200]),
                        }}
                      >
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
                          style={{
                            backgroundColor: done ? EARTH.sage[500] : 'transparent',
                            border: done ? 'none' : '2px solid ' + EARTH.stone[300]
                          }}
                        >
                          {done && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span
                          className="text-sm leading-snug"
                          style={{
                            color: done ? EARTH.stone[500] : EARTH.stone[800],
                            textDecoration: done ? 'line-through' : 'none'
                          }}
                        >
                          {action}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
                <Lightbulb className="w-8 h-8 mx-auto mb-2" style={{ color: EARTH.stone[300] }} />
                <p className="text-sm" style={{ color: EARTH.stone[500] }}>
                  No guidance for this section yet. Try analyzing a track to generate coaching data.
                </p>
              </div>
            )}

            {/* Energy Tip */}
            {energyTip && (
              <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.sand[50], border: '1px solid ' + EARTH.sand[200] }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Zap className="w-3.5 h-3.5" style={{ color: EARTH.sand[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.sand[700] }}>Energy</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[700] }}>{energyTip}</p>
              </div>
            )}

          </>
        )}

        {/* ── MIX TAB ── */}
        {activeTab === 'mix' && (
          <>
            {mixTips.length > 0 ? (
              <div className="space-y-3">
                {mixTips.map((tip, i) => {
                  // Some tips have [Technique Name] suffix
                  const bracketMatch = tip.match(/\[(.+?)\]$/);
                  const tipText = bracketMatch ? tip.replace(bracketMatch[0], '').trim() : tip;
                  const technique = bracketMatch ? bracketMatch[1] : null;
                  return (
                    <div
                      key={i}
                      className="p-3 rounded-xl"
                      style={{
                        backgroundColor: i === 0 ? EARTH.clay[50] : EARTH.stone[50],
                        border: '1px solid ' + (i === 0 ? EARTH.clay[200] : EARTH.stone[200])
                      }}
                    >
                      <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[700] }}>{tipText}</p>
                      {technique && (
                        <span
                          className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ backgroundColor: EARTH.clay[100], color: EARTH.clay[600] }}
                        >
                          {technique}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
                <Sliders className="w-8 h-8 mx-auto mb-2" style={{ color: EARTH.stone[300] }} />
                <p className="text-sm" style={{ color: EARTH.stone[500] }}>No mix tips for this section.</p>
              </div>
            )}
          </>
        )}

        {/* ── FLOW TAB — Arrangement overview + transition tips ── */}
        {activeTab === 'flow' && (
          <>
            {/* Full arrangement — horizontal bar chart */}
            {allSections.length > 0 && (() => {
              const totalBars = allSections.reduce((s, sec) => s + (sec.bars || 8), 0);
              const energyBarColor = (e) =>
                e >= 0.8 ? '#c2412840' : e >= 0.6 ? '#f9731640' : e >= 0.4 ? '#eab30840' :
                e >= 0.2 ? '#22c55e40' : '#94a3b840';
              const energyBorderColor = (e) =>
                e >= 0.8 ? '#c24128' : e >= 0.6 ? '#f97316' : e >= 0.4 ? '#eab308' :
                e >= 0.2 ? '#22c55e' : '#94a3b8';
              return (
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <ArrowRight className="w-3.5 h-3.5" style={{ color: EARTH.forest[600] }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.forest[700] }}>
                      Arrangement Structure
                    </span>
                  </div>
                  <div className="space-y-1.5" style={{ border: '1px solid ' + EARTH.stone[200], borderRadius: '10px', overflow: 'hidden' }}>
                    {allSections.map((sec, i) => {
                      const pct = Math.round(((sec.bars || 8) / totalBars) * 100);
                      const e = sec.energy ?? 0.5;
                      const isCurrent = sec.id === section?.id;
                      return (
                        <div
                          key={sec.id}
                          className="flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors"
                          style={{
                            backgroundColor: isCurrent ? EARTH.forest[50] : (i % 2 === 0 ? '#faf8f5' : '#fff'),
                            borderLeft: isCurrent ? '3px solid ' + EARTH.forest[500] : '3px solid transparent',
                          }}
                          onClick={() => onNavigateToSection?.(sec.id)}
                        >
                          <span className="text-[10px] font-semibold w-16 flex-shrink-0 truncate" style={{ color: isCurrent ? EARTH.forest[700] : EARTH.stone[600] }}>
                            {sec.name.toUpperCase()}
                          </span>
                          <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ backgroundColor: EARTH.stone[100] }}>
                            <div
                              className="h-full rounded-sm transition-all"
                              style={{
                                width: `${pct}%`,
                                background: `linear-gradient(90deg, ${energyBorderColor(e)}cc, ${energyBorderColor(e)}66)`,
                                minWidth: '6px',
                              }}
                            />
                          </div>
                          <span className="text-[10px] w-8 text-right flex-shrink-0" style={{ color: EARTH.stone[400] }}>
                            {sec.bars}b
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Transition tip for this section */}
            {transitionTip && (
              <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.olive[50], border: '1px solid ' + EARTH.olive[200] }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ArrowRight className="w-3.5 h-3.5" style={{ color: EARTH.olive[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.olive[700] }}>Transition Out</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[700] }}>{transitionTip}</p>
              </div>
            )}

            {/* Energy arc context for neighbours */}
            {energyArc.length > 1 && (
              <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.sand[50], border: '1px solid ' + EARTH.sand[200] }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Zap className="w-3.5 h-3.5" style={{ color: EARTH.sand[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.sand[700] }}>Energy Arc</span>
                </div>
                <div className="flex items-end gap-2 h-12">
                  {energyArc.map((item, i) => {
                    const h = Math.max(8, item.energy * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div
                          className="w-full rounded-t"
                          style={{
                            height: `${h}%`,
                            backgroundColor: item.current ? EARTH.terracotta[500] : EARTH.sand[300],
                            minHeight: '4px',
                          }}
                        />
                        <span className="text-[9px] truncate w-full text-center" style={{ color: item.current ? EARTH.stone[700] : EARTH.stone[400] }}>
                          {item.name.length > 7 ? item.name.slice(0, 6) + '…' : item.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!transitionTip && allSections.length === 0 && (
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
                <ArrowRight className="w-8 h-8 mx-auto mb-2" style={{ color: EARTH.stone[300] }} />
                <p className="text-sm" style={{ color: EARTH.stone[500] }}>No arrangement data yet.</p>
              </div>
            )}
          </>
        )}

        {/* ── SOUND TAB ── */}
        {activeTab === 'sound' && (
          <>
            {/* Elements in this section */}
            {section.elements?.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Layers className="w-3.5 h-3.5" style={{ color: EARTH.forest[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.forest[700] }}>Elements</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {section.elements.map((el, i) => (
                    <span
                      key={i}
                      className="text-xs px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: EARTH.forest[50], color: EARTH.forest[700], border: '1px solid ' + EARTH.forest[200] }}
                    >
                      {el}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Reference Tracks */}
            {referenceTracks.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Music className="w-3.5 h-3.5" style={{ color: EARTH.terracotta[600] }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: EARTH.terracotta[700] }}>Reference Tracks</span>
                </div>
                <div className="space-y-1.5">
                  {referenceTracks.map((track, i) => (
                    <div
                      key={i}
                      className="text-sm p-2 rounded-lg"
                      style={{ backgroundColor: EARTH.terracotta[50], color: EARTH.stone[700], border: '1px solid ' + EARTH.terracotta[200] }}
                    >
                      {track}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!section.elements?.length && !referenceTracks.length && (
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
                <Volume2 className="w-8 h-8 mx-auto mb-2" style={{ color: EARTH.stone[300] }} />
                <p className="text-sm" style={{ color: EARTH.stone[500] }}>No sound design data for this section.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer — actions + navigation */}
      <div className="p-4 border-t space-y-3" style={{ borderColor: EARTH.stone[200] }}>
        {/* Mark Complete */}
        {guidance && (
          <button
            onClick={() => {
              if (onUpdateSectionStatus) {
                onUpdateSectionStatus(section.id, allStepsCompleted ? 'done' : 'in-progress');
              }
              if (allStepsCompleted && nextSection && onNavigateToSection) {
                onNavigateToSection(nextSection.id);
              }
            }}
            className="w-full py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: allStepsCompleted ? EARTH.sage[600] : EARTH.forest[600],
              color: '#fff',
            }}
          >
            {allStepsCompleted ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />
                Mark Complete & Next
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Circle className="w-4 h-4" />
                Mark In Progress
              </span>
            )}
          </button>
        )}

        {/* Prev / Next nav */}
        <div className="flex gap-2">
          <button
            onClick={() => prevSection && onNavigateToSection?.(prevSection.id)}
            disabled={!prevSection}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors"
            style={{
              backgroundColor: prevSection ? EARTH.stone[100] : EARTH.stone[50],
              color: prevSection ? EARTH.stone[700] : EARTH.stone[300],
              border: '1px solid ' + EARTH.stone[200]
            }}
          >
            <ArrowLeft className="w-3 h-3" />
            Prev
          </button>
          <button
            onClick={() => nextSection && onNavigateToSection?.(nextSection.id)}
            disabled={!nextSection}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors"
            style={{
              backgroundColor: nextSection ? EARTH.stone[100] : EARTH.stone[50],
              color: nextSection ? EARTH.stone[700] : EARTH.stone[300],
              border: '1px solid ' + EARTH.stone[200]
            }}
          >
            Next
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export { CoachingSidebar };
